import { PaginationData } from '../models/paginationData';
import {
    User, UserDetail, RewardHistoryItem, PointPurchaseItem, SubscriptionHistoryItem,
    UserReportItem, UserInquiryItem, UserPushHistoryItem,
    MasteredWordItem, MissionClaimItem, EppiDetail, FeatherHistoryItem, AdminAiCreditDetail,
} from '../types/user';
import { UserFilterParams } from '../models/user/userFilterParams';
import {
    AdjustUserPointRequest, ChangeUserPasswordRequest, DeleteUserRequest,
    UpdateUserRoleRequest, UpdateUserSubscriptionRequest,
    AdjustFeathersRequest, AdjustCreditsRequest,
} from '../models/user/userRequests';
import {
    UserOverviewStatsResponse,
    UserProviderStatsResponse,
    UserSubscriptionStatsResponse
} from '../models/user/userResponses';
import { apiClient } from './axios';

export const userApi = {
    // 사용자 목록 조회
    getList: (params: UserFilterParams) =>
        apiClient.get<PaginationData<User>>('/admin/users', {
            page: params.page - 1, // API는 0-indexed
            size: params.size,
            sortBy: params.sortBy,
            sortDirection: params.sortDirection,
            searchKeyword: params.searchKeyword || undefined,
            role: params.role || undefined,
            provider: params.provider || undefined,
            subscriptionType: params.subscriptionType || undefined,
            isSubscriptionActive: params.isSubscriptionActive,
            includeDeleted: params.includeDeleted ?? false,
        }),

    // 사용자 상세 조회
    getOne: (id: number) =>
        apiClient.get<UserDetail>(`/admin/users/${id}`),

    // 사용자 삭제
    delete: (id: number, data: DeleteUserRequest) =>
        apiClient.deleteNoData(`/admin/users/${id}`, data),

    // 사용자 복원
    restore: (id: number) =>
        apiClient.postNoData(`/admin/users/${id}/restore`, {}),

    // 사용자 역할 변경
    updateRole: (id: number, data: UpdateUserRoleRequest) =>
        apiClient.patchNoData(`/admin/users/${id}/role`, data),

    // 사용자 구독 변경
    updateSubscription: (id: number, data: UpdateUserSubscriptionRequest) =>
        apiClient.patchNoData(`/admin/users/${id}/subscription`, data),

    // 사용자 포인트 조정
    adjustPoints: (id: number, data: AdjustUserPointRequest) =>
        apiClient.patchNoData(`/admin/users/${id}/points`, data),

    // 사용자 비밀번호 변경 (이메일 가입자 전용)
    changePassword: (id: number, data: ChangeUserPasswordRequest) =>
        apiClient.patchNoData(`/admin/users/${id}/password`, data),

    // 포인트 획득/차감 이력 조회
    getRewardHistories: (id: number, page = 0, size = 30) =>
        apiClient.get<PaginationData<RewardHistoryItem>>(`/admin/users/${id}/reward-histories`, { page, size }),

    // 포인트 구매 이력 조회
    getPointPurchases: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<PointPurchaseItem>>(`/admin/users/${id}/point-purchases`, { page, size }),

    // 구독 이력 조회
    getSubscriptionHistories: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<SubscriptionHistoryItem>>(`/admin/users/${id}/subscription-histories`, { page, size }),

    // 사용자 신고 이력
    getReports: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<UserReportItem>>(`/admin/users/${id}/reports`, { page, size }),

    // 사용자 문의 이력
    getInquiries: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<UserInquiryItem>>(`/admin/users/${id}/inquiries`, { page, size }),

    // 사용자에게 발송된 푸시 이력 (개별 발송)
    getPushHistories: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<UserPushHistoryItem>>(`/admin/users/${id}/push-histories`, { page, size }),

    // 마스터 단어 목록
    getMasteredWords: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<MasteredWordItem>>(`/admin/users/${id}/mastered-words`, { page, size }),

    // 미션 claim 목록
    getMissionClaims: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<MissionClaimItem>>(`/admin/users/${id}/mission-claims`, { page, size }),

    // 에삐(스티커북) 상세 — 목록형 필드는 NON_EMPTY로 키 자체가 누락될 수 있어 기본값 보정
    getEppiDetail: async (id: number): Promise<EppiDetail> => {
        const data = await apiClient.get<EppiDetail>(`/admin/users/${id}/eppi`);
        return {
            ...data,
            equippedSlots: data.equippedSlots ?? [],
            ownedCostumes: data.ownedCostumes ?? [],
            roomItems: data.roomItems ?? [],
        };
    },

    // 깃털(XP) 내역
    getFeatherHistories: (id: number, page = 0, size = 20) =>
        apiClient.get<PaginationData<FeatherHistoryItem>>(`/admin/users/${id}/feather-histories`, { page, size }),

    // AI 크레딧 상세 (잔액 + 이력 + 사용처 집계)
    getAiCreditDetail: async (id: number): Promise<AdminAiCreditDetail> => {
        const data = await apiClient.get<AdminAiCreditDetail>(`/admin/users/${id}/ai-credits`);
        return {
            ...data,
            history: data.history ?? [],
            usageByReason: data.usageByReason ?? [],
        };
    },

    // AI 크레딧 조정
    adjustCredits: (id: number, data: AdjustCreditsRequest) =>
        apiClient.post<AdminAiCreditDetail>(`/admin/users/${id}/ai-credits/adjust`, data),

    // 깃털 조정
    adjustFeathers: (id: number, data: AdjustFeathersRequest) =>
        apiClient.patch<UserDetail>(`/admin/users/${id}/feathers`, data),

    // 전체 통계
    getOverviewStats: () =>
        apiClient.get<UserOverviewStatsResponse>('/admin/users/stats/overview'),

    // 제공자별 통계
    getProviderStats: () =>
        apiClient.get<UserProviderStatsResponse>('/admin/users/stats/by-provider'),

    // 구독 통계
    getSubscriptionStats: () =>
        apiClient.get<UserSubscriptionStatsResponse>('/admin/users/stats/by-subscription'),
};
